import React from 'react'
import { CircleFill, Headphones, Instagram, Linkedin, Pinterest, Stars, Tiktok, Youtube } from 'react-bootstrap-icons'

const providers = ['instagram', 'tiktok', 'youtube', 'pinterest', 'linkedin', 'podcast', 'other']

export const actionTypeLabels = {
  instagram_reel: 'Reel',
  instagram_story: 'Story',
  instagram_post: 'Post',
  instagram_carousel_post: 'Carousel Post',
  tiktok_video: 'Video',
  youtube_video: 'Video',
  youtube_short: 'Shorts',
  linkedin_post: 'Beitrag',
  linkedin_video: 'Video',
  linkedin_newsletter: 'Newsletter',
  pinterest_pin: 'Pin',
  podcast_episode: 'Episode',
  other_item: 'Collaboration',
  non_media_item: 'Non-media item',
}

export const providerIconClassNames = {
  instagram: 'text-pink-500 dark:text-pink-400',
  tiktok: 'text-black dark:text-white',
  youtube: 'text-red-500 dark:text-red-400',
  pinterest: 'text-red-600 dark:text-red-400',
  linkedin: 'text-rb-linkedin dark:text-blue-400',
  podcast: 'text-gray-700 dark:text-gray-300',
  other: 'text-amber-600 dark:text-amber-400',
}

export const providerAccentColors = {
  instagram: '#ec4899',
  tiktok: '#111827',
  youtube: '#ef4444',
  pinterest: '#dc2626',
  linkedin: '#0a66c2',
  podcast: '#4b5563',
  other: '#d97706',
}

export const actionTypeAccentColors = {
  instagram_reel: '#ec4899',
  instagram_post: '#db2777',
  instagram_carousel_post: '#c026d3',
  instagram_story: '#f9a8d4',
  tiktok_video: '#111827',
  youtube_video: '#ef4444',
  youtube_short: '#f97316',
  linkedin_post: '#0a66c2',
  linkedin_video: '#38bdf8',
  linkedin_newsletter: '#6366f1',
  pinterest_pin: '#dc2626',
  podcast_episode: '#4b5563',
  other_item: '#d97706',
}

const fallbackAccentColor = '#50d47f'

const providerIcons = {
  instagram: Instagram,
  tiktok: Tiktok,
  youtube: Youtube,
  pinterest: Pinterest,
  linkedin: Linkedin,
  podcast: Headphones,
  other: Stars,
}

function titleize(value) {
  if (!value) return ''

  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function providerFromActionType(actionType) {
  if (!actionType) return null
  if (actionType === 'other_item') return 'other'
  if (actionType.startsWith('podcast_')) return 'podcast'
  return providers.find((provider) => actionType.startsWith(`${provider}_`))
}

export function actionProviderFor(asset) {
  return asset?.provider || providerFromActionType(asset?.action_type)
}

export function actionLabelFor(asset) {
  if (!asset?.action_type) return ''

  if (actionTypeLabels[asset.action_type]) return actionTypeLabels[asset.action_type]

  const provider = actionProviderFor(asset)
  const actionWithoutProvider = provider
    ? asset.action_type.replace(new RegExp(`^${provider}_`), '')
    : asset.action_type

  return titleize(actionWithoutProvider)
}

export function actionDisplayLabelFor(asset) {
  const actionLabel = actionLabelFor(asset)
  const actionName = asset?.name?.trim()
  if (!actionName) return actionLabel

  const provider = actionProviderFor(asset)
  const providerLabel = titleize(provider)
  const providerPrefixedActionLabel = [providerLabel, actionLabel].filter(Boolean).join(' ')

  if (providerPrefixedActionLabel && actionName.startsWith(providerPrefixedActionLabel)) {
    return actionName.slice(providerLabel.length).trim()
  }

  if (actionLabel && actionName.startsWith(actionLabel)) return actionName

  return actionLabel
}

export function actionPrimaryLabelFor(asset, { showAuthUsername = true } = {}) {
  const username = showAuthUsername ? asset?.auth_username || asset?.username : ''
  const label = username || asset?.name || 'Untitled asset'
  // Duplicate-row actions (slotNumber > 1, see the Budget Table "(extra N)" row) are flagged here too,
  // since the timeline doesn't otherwise distinguish which row a scheduled piece belongs to.
  const slotNumber = Number(asset?.slot_number) || 1
  return slotNumber > 1 ? `${label} (extra ${slotNumber - 1})` : label
}

export function actionFullLabelFor(asset, options) {
  const primaryLabel = actionPrimaryLabelFor(asset, options)
  const actionLabel = actionDisplayLabelFor(asset)
  return actionLabel ? `${primaryLabel} - ${actionLabel}` : primaryLabel
}

export function actionAccentColorFor(asset) {
  const provider = actionProviderFor(asset)
  return actionTypeAccentColors[asset?.action_type] || providerAccentColors[provider] || fallbackAccentColor
}

export function TimelineActionLabel({
  asset,
  showAuthUsername = true,
  passed = false,
  className = '',
}) {
  const actionLabel = actionDisplayLabelFor(asset)
  const primaryLabel = actionPrimaryLabelFor(asset, { showAuthUsername })
  const fullLabel = actionFullLabelFor(asset, { showAuthUsername })
  const actionColor = actionAccentColorFor(asset)

  return (
    <h4
      className={`flex min-w-0 flex-1 items-baseline overflow-hidden ${className}`}
      title={fullLabel}
    >
      <span className="min-w-0 flex-1 truncate" data-timeline-primary-label>
        {primaryLabel}
      </span>
      {actionLabel && (
        <span className="shrink-0 whitespace-nowrap">
          <span className="px-1 text-rb-lighter-grey dark:text-gray-400">-</span>
          <span
            className={`font-semibold ${
              passed ? 'text-rb-lighter-grey dark:text-gray-400' : ''
            }`}
            style={passed ? undefined : { color: actionColor }}
          >
            {actionLabel}
          </span>
        </span>
      )}
    </h4>
  )
}

export function TimelineActionProviderIcon({ asset, className = '', style }) {
  const provider = actionProviderFor(asset)
  const ProviderIcon = providerIcons[provider]

  if (!ProviderIcon) {
    return (
      <CircleFill
        aria-hidden="true"
        className={`shrink-0 text-[6px] text-rb-green ${className}`}
        style={style}
      />
    )
  }

  return (
    <ProviderIcon
      aria-hidden="true"
      className={`shrink-0 ${providerIconClassNames[provider]} ${className}`}
      style={style}
    />
  )
}
